import React, { useState, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { Item } from "../../types";
import {
    searchProducts,
    ProductData,
    productToItem,
} from "../../utils/productData";
import "./ItemForm.css";

interface ItemFormProps {
    onAddItem: (item: Item) => void;
}

const ItemForm: React.FC<ItemFormProps> = ({ onAddItem }) => {
    const [name, setName] = useState<string>("");
    const [quantity, setQuantity] = useState<number>(1);
    const [price, setPrice] = useState<number>(0);
    const [error, setError] = useState<string>("");
    const [searchResults, setSearchResults] = useState<ProductData[]>([]);
    const [showResults, setShowResults] = useState<boolean>(false);
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const resultsContainerRef = useRef<HTMLDivElement>(null);
    const nameInputRef = useRef<HTMLInputElement>(null);

    // Handle product search with debounce
    const handleSearch = (query: string) => {
        setName(query);
        setShowResults(query.length > 0);

        // Clear previous timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Set new timeout for debounce
        if (query.length > 1) {
            setIsSearching(true);
            searchTimeoutRef.current = setTimeout(() => {
                const results = searchProducts(query);
                setSearchResults(results);
                setIsSearching(false);
            }, 300);
        } else {
            setSearchResults([]);
        }
    };

    // Listen for the custom product selection event
    useEffect(() => {
        const handleProductSelectedEvent = (event: any) => {
            const { name, price, quantity } = event.detail;
            console.log(
                "Handling product selected event:",
                name,
                price,
                quantity
            );

            // Update form fields
            setName(name);
            setPrice(price);
            setQuantity(quantity);
        };

        window.addEventListener("product-selected", handleProductSelectedEvent);

        return () => {
            window.removeEventListener(
                "product-selected",
                handleProductSelectedEvent
            );
        };
    }, []);

    // Close search results when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                resultsContainerRef.current &&
                !resultsContainerRef.current.contains(event.target as Node)
            ) {
                setShowResults(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Simple function to handle product selection
    const selectProduct = (product: ProductData) => {
        const item = productToItem(product);
        setName(item.name);
        setPrice(item.price);
        setQuantity(1);
        setSearchResults([]);
        setShowResults(false);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        // Validate inputs
        if (!name.trim()) {
            setError("Item name is required");
            return;
        }

        if (quantity <= 0) {
            setError("Quantity must be greater than 0");
            return;
        }

        if (price <= 0) {
            setError("Price must be greater than 0");
            return;
        }

        // Create new item
        const newItem: Item = {
            id: uuidv4(),
            name: name.trim(),
            quantity,
            price,
        };

        // Add item to list
        onAddItem(newItem);

        // Reset form
        setName("");
        setQuantity(1);
        setPrice(0);
        setError("");

        // Focus on name input field for rapid entry
        setTimeout(() => {
            if (nameInputRef.current) {
                nameInputRef.current.focus();
            }
        }, 0);
    };

    return (
        <div className="item-form-container">
            {error && <div className="error-message">{error}</div>}
            <form onSubmit={handleSubmit} className="item-form">
                <div className="form-main-row">
                    <div className="form-group item-name-group">
                        <label htmlFor="name">Item Name</label>
                        <div
                            className="autocomplete-container"
                            ref={resultsContainerRef}
                        >
                            <input
                                type="text"
                                id="name"
                                value={name}
                                onChange={(e) => handleSearch(e.target.value)}
                                placeholder="Enter item name or search products"
                                required
                                autoComplete="off"
                                ref={nameInputRef}
                            />
                            {showResults && (
                                <div className="search-results">
                                    {isSearching ? (
                                        <div className="search-status">
                                            Searching...
                                        </div>
                                    ) : searchResults.length > 0 ? (
                                        searchResults.map((product) => (
                                            <div
                                                key={`${product.product}-${product.title}`}
                                                className="search-result-item"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    // Call selectProduct with the product
                                                    selectProduct(product);
                                                    // Force focus back to the name input after selection
                                                    if (nameInputRef.current) {
                                                        nameInputRef.current.focus();
                                                    }
                                                }}
                                            >
                                                <div className="product-info">
                                                    <div className="product-title">
                                                        {product.title}
                                                    </div>
                                                    <div className="product-price">
                                                        ৳
                                                        {product.discounted_price ||
                                                            product.price}
                                                        {product.discounted_price !==
                                                            product.price &&
                                                            product.price >
                                                                0 && (
                                                                <span className="original-price">
                                                                    ৳
                                                                    {
                                                                        product.price
                                                                    }
                                                                </span>
                                                            )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    ) : name.length > 1 ? (
                                        <div className="search-status">
                                            No products found
                                        </div>
                                    ) : null}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="form-input-group">
                        <div className="input-fields">
                            <div className="form-group quantity-group">
                                <label htmlFor="quantity">Qty</label>
                                <input
                                    type="number"
                                    id="quantity"
                                    value={quantity}
                                    onChange={(e) =>
                                        setQuantity(Number(e.target.value))
                                    }
                                    min="1"
                                    step="1"
                                    required
                                />
                            </div>

                            <div className="form-group price-group">
                                <label htmlFor="price">Price (Tk)</label>
                                <input
                                    type="number"
                                    id="price"
                                    value={price}
                                    onChange={(e) =>
                                        setPrice(Number(e.target.value))
                                    }
                                    min="0.01"
                                    step="0.01"
                                    required
                                />
                            </div>
                        </div>

                        <button type="submit" className="add-item-button">
                            Add Item
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default ItemForm;
